import { useRef, useState } from 'react';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { useDismiss } from '@/hooks/useDismiss';
import { usePopoverSide } from '@/hooks/usePopoverSide';
import { cx } from '@/utils/cx';
import { formatDayLabel, fromDateKey, toDateKey, todayKey } from '@/utils/date';
import { Button } from './Button';
import styles from '@/styles/components/DatePicker/style.scss';

interface DatePickerProps {
  /** YYYY-MM-DD */
  value: string;
  onChange: (value: string) => void;
  /** Accessible name, e.g. "Day opened". */
  label: string;
  /** Latest selectable day (YYYY-MM-DD). Defaults to today. */
  max?: string;
  /** Shown above the field, like a text field's label (for forms). */
  fieldLabel?: string;
  /**
   * The calendar opens in the page flow under the field instead of floating over it. For form modals, whose body
   * would clip a floating calendar.
   */
  inline?: boolean;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** A field-styled button showing the day ("Today", "Sat, 3 Oct") that opens a month calendar. */
export function DatePicker({ value, onChange, label, max = todayKey(), fieldLabel, inline = false }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfMonth(fromDateKey(value)));
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const side = usePopoverSide(open, rootRef, popoverRef);

  const close = () => setOpen(false);
  useDismiss(open, close, rootRef);

  const toggle = () => {
    if (!open) setMonth(startOfMonth(fromDateKey(value)));
    setOpen(!open);
  };

  const pick = (key: string) => {
    onChange(key);
    close();
  };

  const today = todayKey();
  const maxMonth = startOfMonth(fromDateKey(max));
  const canGoNext = month < maxMonth;
  const days = monthGrid(month);

  return (
    <div ref={rootRef} className={cx(styles.root, fieldLabel && styles.labelled)}>
      {fieldLabel && <span className={styles.fieldLabel}>{fieldLabel}</span>}
      <button type="button" className={styles.trigger} aria-haspopup="dialog" aria-expanded={open} aria-label={`${label}: ${formatDayLabel(value)}`} onClick={toggle}>
        <CalendarIcon size={16} />
        <span className={styles.value}>{formatDayLabel(value)}</span>
        <ChevronDownIcon size={16} />
      </button>

      {open && (
        <div ref={popoverRef} role="dialog" aria-label={label} className={cx(styles.popover, inline ? styles.inline : side === 'left' && styles.alignLeft)}>
          <div className={styles.head}>
            <Button size="sm" variant="ghost" iconOnly icon={<ChevronLeftIcon size={16} />} aria-label="Previous month" onClick={() => setMonth(addMonths(month, -1))} />
            <span className={styles.month}>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
            <Button size="sm" variant="ghost" iconOnly icon={<ChevronRightIcon size={16} />} aria-label="Next month" onClick={() => setMonth(addMonths(month, 1))} disabled={!canGoNext} />
          </div>

          <div className={styles.grid} role="grid">
            {WEEKDAYS.map((w) => (
              <span key={w} className={styles.weekday} aria-hidden>
                {w}
              </span>
            ))}
            {days.map((d, i) => {
              if (!d) return <span key={`blank-${i}`} />;
              const key = toDateKey(d);
              const disabled = key > max;
              return (
                <button
                  key={key}
                  type="button"
                  className={cx(styles.day, key === value && styles.selected, key === today && styles.today)}
                  aria-pressed={key === value}
                  aria-label={d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  disabled={disabled}
                  onClick={() => pick(key)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.foot}>
            <Button size="sm" variant="outline" onClick={() => pick(today)} disabled={value === today}>
              Today
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** The month's days with leading blanks so the 1st lands under its weekday (weeks start on Sunday). */
function monthGrid(month: Date): (Date | null)[] {
  const blanks = Array<null>(month.getDay()).fill(null);
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1));
  return [...blanks, ...days];
}
